import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Clock, ExternalLink, Search, Settings, Tag, XCircle } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';

import {
  AssignmentStatus,
  Gender,
  GetPurchaseStatsQuery as GetPurchaseStats,
  GetRegistrationFiltersQuery as GetRegistrationFilters,
  Locale,
  ParticipantFieldScope,
  Permission,
  PurchasePaymentStatus,
  PurchaseStatus,
  RegistrationFilters as RegistrationFiltersType,
} from '__generated__/graphql';

import { UrlState, parseHelpers } from '../../../common/useUrlState';
import { encodeUrl, isEmpty, toggleInList } from '../../../common/helpers';
import {
  syncParticipantFields, syncProductVariants, syncPromotions, syncTimeSlots,
} from '../../../common/filterHelpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetPurchaseStatsQuery from '../GetPurchaseStatsQuery';
import GetRegistrationFiltersQuery from './GetRegistrationFiltersQuery';
import ParticipantFieldFiltersForm, {
  ParticipantFieldFilters,
} from '../ParticipantFieldFiltersForm';
import ProtectedSection from '../../Common/ProtectedSection';
import SellableLabel from '../../../common/Common/SellableLabel';
import UI from '../../../common/UI';
import config from '../../../config';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

type Event = GetRegistrationFilters['project']['events']['data'][0];

export type PurchaseStatsData = GetPurchaseStats['project']['purchase_stats'];

export interface ParticipantFieldFilter {
  id: string;
  filters?: ParticipantFieldFilters;
}

export interface RegistrationFilterParams {
  event?: string | null;
  search?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  locales?: Locale[];
  genders?: Gender[];
  mail_opt_ins?: string[];
  promotions?: string[];
  ticket_categories?: string[];
  tickets?: string[];
  time_slots?: string[];
  products?: string[];
  product_variants?: string[];
  created_after?: string | null;
  created_before?: string | null;
  statuses?: PurchaseStatus[];
  assignment_statuses?: AssignmentStatus[];
  payment_statuses?: PurchasePaymentStatus[];
  participant_fields?: ParticipantFieldFilter[];
  participant_field_choices?: string[];
  ticket_number_sequences?: string[];
  has_ticket_number?: boolean | null,
  has_team?: boolean | null,
  invitations?: string[];
  coupons?: string[];
  lotteries?: string[];
  rejected_lotteries?: string[];
}

/**
 * Returns the participant fields that can be filtered on for the active event, tickets and products
 */
const getParticipantFields = (event: Event, ticketIds: string[], productIds: string[]) => {
  const tickets = event.ticket_categories.reduce((tickets, ticketCategory) => [...tickets, ticketCategory.tickets], []);

  return event.participant_fields.filter((field) => {
    if (field.scope !== ParticipantFieldScope.purchase) {
      return false;
    }

    const requiredTicketIds = tickets.filter(({ id }) => (
      field.tickets.map(({ id }) => id).includes(id)
    ));

    const requiredProductIds = event.products.filter(({ id }) => (
      field.products.map(({ id }) => id).includes(id)
    ));

    return (requiredTicketIds.length === 0
          || intersection(ticketIds, field.tickets.map(({ id }) => id)).length > 0)
        && (requiredProductIds.length === 0
          || intersection(productIds, field.products.map(({ id }) => id)).length > 0);
  });
};

export type RegistrationFilterType = (
  'event' | 'search' | 'age' | 'gender' | 'mail_opt_ins' | 'tickets' | 'products' | 'team'
  | 'statuses' | 'assignment_statuses' | 'payment_statuses' | 'participant_fields'
  | 'ticket_number_sequences' | 'invitations' | 'coupons' | 'lotteries' | 'rejected_lotteries'
  | 'locales' | 'registration_date'
);

export interface RegistrationFiltersProps {
  types?: RegistrationFilterType[];
  filters: RegistrationFilterParams;
  onChange: (filters: RegistrationFilterParams) => unknown;
  onStatsChange?: (stats: PurchaseStatsData) => void;
  showTotals?: boolean;
  showResetButton?: boolean;
  showViewRegistrationsButton?: boolean;
  readOnly?: boolean;
}

const RegistrationFilters = ({
  types, filters, onChange, onStatsChange, showTotals = true, showResetButton = true,
  showViewRegistrationsButton = false, readOnly = false,
}: RegistrationFiltersProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const enabled = (type: RegistrationFilterType) => !types || types.includes(type);

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters (tickets etc.)
  const { data: filtersData } = useQuery(
    GetRegistrationFiltersQuery,
    {
      variables: {
        project_id: project.id,
        events: filters.event ? [filters.event] : [],
      },
      fetchPolicy: 'cache-first',
    },
  );

  const mailPermissions = filtersData?.project.mail_permissions.filter(
    (mailPermission) => !mailPermission.integration,
  ) || [];
  const events = filtersData?.project.events.data || [];
  const event = events.filter(({ id }) => filters.event === id)[0] || null;
  const products = event?.products.filter((product) => !product.stand_alone) || [];

  const participantFields = useMemo(
    () => (event ? getParticipantFields(event, filters.tickets, filters.products) : []),
    [event, filters.tickets, filters.products],
  );
  const handleChange = useCallback((filters: RegistrationFilterParams) => {
    let newFilters = filters;
    newFilters = syncTimeSlots(newFilters, event);
    newFilters = syncProductVariants(newFilters, event);
    newFilters = syncPromotions(newFilters, event);
    newFilters = syncParticipantFields(newFilters, participantFields);
    onChange(newFilters);
  }, [event, participantFields, onChange]);

  const { mapRegistrationFiltersToServerInput } = useRegistrationFilterMapping();

  // Fetches the stats
  const { data: statsData, loading: loadingStats } = useQuery(
    GetPurchaseStatsQuery,
    {
      variables: {
        ...mapRegistrationFiltersToServerInput(filters),
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      skip: !showTotals,
      onCompleted: (data) => {
        onStatsChange?.(data.project.purchase_stats);
      },
    },
  );

  const stats = statsData?.project.purchase_stats;

  const getDefaultShowingFilters = (filters: RegistrationFilterParams) => ({
    age: !!filters.min_age || !!filters.max_age,
    locales: filters.locales?.length > 0,
    genders: filters.genders?.length > 0,
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    tickets: filters.ticket_categories?.length > 0 || filters.tickets?.length > 0 || filters.time_slots?.length
      || filters.promotions?.length > 0,
    products: filters.products?.length > 0 || filters.product_variants?.length > 0 || filters.promotions?.length > 0,
    registration_date: filters.created_after || filters.created_before,
    statuses: filters.statuses?.length > 0,
    assignment_statuses: filters.assignment_statuses?.length > 0,
    payment_statuses: filters.payment_statuses?.length > 0,
    participant_fields: filters.participant_fields?.length > 0 || filters.participant_field_choices?.length > 0,
    ticket_number_sequences: filters.ticket_number_sequences?.length > 0 || filters.has_ticket_number,
    team: filters.has_team,
    invitations: filters.invitations?.length > 0,
    coupons: filters.coupons?.length > 0,
    lotteries: filters.lotteries?.length > 0,
    rejected_lotteries: filters.rejected_lotteries?.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    // Reset all filters except search.
    handleChange({
      search: filters.search,
      event: event?.id,
    });
  };

  const handleCreatedAfterChange = (date: string | null) => {
    handleChange({
      ...filters,
      created_after: date,
    });
  };

  const handleCreatedBeforeChange = (date: string | null) => {
    handleChange({
      ...filters,
      created_before: date,
    });
  };

  /**
   * Handles changes to the min age input by storing the value in the filters.
   */
  const handleMinAgeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    handleChange({
      ...filters,
      min_age: value ? parseInt(value, 10) : null,
    });
  };

  /**
   * Handles changes to the min age input by storing the value in the filters.
   */
  const handleMaxAgeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    handleChange({
      ...filters,
      max_age: value ? parseInt(value, 10) : null,
    });
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange({
      ...filters,
      search: event.target.value || null,
    });
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    handleChange({
      ...filters,
      search: null,
    });

    searchRef.current.value = '';
  };

  // Only the keys of RegistrationFilterParams that have an array as value
  type RegistrationFilterListType = {
    [K in keyof RegistrationFilterParams]-?: RegistrationFilterParams[K] extends Array<any> ? K : never
  }[keyof RegistrationFilterParams];

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (name: RegistrationFilterListType, id: string) => {
    handleChange({
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    });
  };

  /**
   * Toggles a gender filter.
   */
  const toggleGenderFilter = (gender?: Gender) => {
    toggleListFilter('genders', gender);
  };

  /**
   * Toggles a locale filter.
   */
  const toggleLocaleFilter = (locale?: Locale) => {
    toggleListFilter('locales', locale);
  };

  /**
   * Toggles a ticket category filter.
   */
  const toggleTicketCategoryFilter = (ticketCategoryId: string) => {
    toggleListFilter('ticket_categories', ticketCategoryId);
  };

  /**
   * Toggles a ticket filter.
   */
  const toggleTicketFilter = (ticketId: string) => {
    if (event.ticket_categories.length === 1) {
      const newTicketIds = toggleInList([...filters.tickets || []], ticketId);

      // If there is only one ticket category, the ticket category filter is not shown,
      // so it must be added/removed automatically.
      handleChange({
        ...filters,
        ticket_categories: newTicketIds.length > 0 ? [event.ticket_categories[0].id] : [],
        tickets: newTicketIds,
      });
    } else {
      toggleListFilter('tickets', ticketId);
    }
  };

  /**
   * Toggles a time slot filter.
   */
  const toggleTimeSlotFilter = (timeSlotId: string) => {
    toggleListFilter('time_slots', timeSlotId);
  };

  /**
   * Toggles a product filter.
   */
  const toggleProductFilter = (productId: string) => {
    toggleListFilter('products', productId);
  };

  /**
   * Toggles an invitation code filter.
   */
  const toggleInvitationFilter = (invitationId: string) => {
    toggleListFilter('invitations', invitationId);
  };

  /**
   * Toggles a coupon filter.
   */
  const toggleCouponFilter = (couponId: string) => {
    toggleListFilter('coupons', couponId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleLotteryFilter = (lotteryId: string) => {
    toggleListFilter('lotteries', lotteryId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleRejectedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('rejected_lotteries', lotteryId);
  };

  /**
   * Toggles a product variant filter.
   */
  const toggleProductVariantFilter = (productVariantId: string) => {
    toggleListFilter('product_variants', productVariantId);
  };

  /**
   * Toggles a promotion filter.
   */
  const togglePromotionFilter = (promotionId: string) => {
    toggleListFilter('promotions', promotionId);
  };

  /**
   * Toggles a purchase status filter.
   */
  const toggleStatusFilter = (status: PurchaseStatus) => {
    toggleListFilter('statuses', status);
  };

  /**
   * Toggles an assignment status filter.
   */
  const toggleAssignmentStatusFilter = (status: AssignmentStatus) => {
    toggleListFilter('assignment_statuses', status);
  };

  /**
   * Toggles a payment status filter.
   */
  const togglePaymentStatusFilter = (status: PurchasePaymentStatus) => {
    toggleListFilter('payment_statuses', status);
  };

  /**
   * Toggles a participant field filter.
   */
  const toggleParticipantFieldFilter = (participantFieldId: string) => {
    const participantFieldFilters = filters.participant_fields || [];
    const index = participantFieldFilters.findIndex(({ id }) => id === participantFieldId);

    const participantFields = index === -1
      ? [...participantFieldFilters, { id: participantFieldId }]
      : participantFieldFilters.filter(({ id }) => id !== participantFieldId);

    handleChange({
      ...filters,
      participant_fields: participantFields,
    });
  };

  /**
   * Updates the filter object for a participant field.
   */
  const setParticipantFieldFilters = (
    fieldId: string,
    update: (fieldFilters: ParticipantFieldFilters) => ParticipantFieldFilters,
  ) => {
    handleChange({
      ...filters,
      participant_fields: filters.participant_fields?.map((field) => (field.id === fieldId ? {
        ...field,
        filters: update(field.filters),
      } : field)),
    });
  };

  /**
   * Toggles a participant field choice filter.
   */
  const toggleParticipantFieldChoiceFilter = (participantFieldChoiceId: string) => {
    toggleListFilter('participant_field_choices', participantFieldChoiceId);
  };

  /**
   * Toggles a ticket number sequence filter.
   */
  const toggleTicketNumberSequenceFilter = (sequenceId: string) => {
    toggleListFilter('ticket_number_sequences', sequenceId);
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, the UI state and search input
   */
  const resetFilters = (newFilters: RegistrationFilterParams = {}) => {
    handleChange(newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters?.search || '';
    }
  };

  const selectedActiveStatus = !!filters.statuses?.includes(PurchaseStatus.complete)
    || !!filters.statuses?.includes(PurchaseStatus.incomplete);

  const toggleActiveFilter = () => {
    if (selectedActiveStatus) {
      handleChange({
        ...filters,
        statuses: difference(filters.statuses || [], [
          PurchaseStatus.complete, PurchaseStatus.incomplete,
        ]),
      });
    } else {
      handleChange({
        ...filters,
        statuses: [
          ...filters.statuses || [],
          PurchaseStatus.complete,
          PurchaseStatus.incomplete,
        ].sort(),
      });
    }
  };

  const toggleHasTeamFilter = (value: boolean | null) => {
    handleChange({
      ...filters,
      has_team: value,
    });
  };
  const toggleHasTicketNumberFilter = () => {
    // Toggle between 'false' and 'null'.
    // Setting 'has_ticket_number' to 'true' is not possible in the interface.
    // This can be achieved by selecting ticket number sequences instead.
    handleChange({
      ...filters,
      has_ticket_number: filters.has_ticket_number === false ? null : false,
    });
  };

  return (
    <UI.FormGrid sc={{ gutter: 2 / 3 }}>
      <UI.Delimit delimiter={<UI.HR sc={{ dotted: true }} />}>
        {(enabled('search') || enabled('event')) && (
          <>
            {enabled('event') && (
              <UI.Div>
                <EventPicker
                  eventId={filters.event}
                  onChange={selectEvent}
                  renderActive={(event) => (
                    <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                      {event?.title || t('all_events')}
                    </UI.Span>
                  )}
                />
              </UI.Div>
            )}

            {enabled('search') && (
              <UI.InputGroup>
                <UI.InputWrapper>
                  <UI.Span>
                    <UI.Icon>
                      <Search />
                    </UI.Icon>
                  </UI.Span>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.search}
                    onChange={handleSearch}
                    placeholder={t('search_registrations')}
                    ref={searchRef}
                    sc={{ px: 5 }}
                    disabled={readOnly}
                  />
                  {filters.search && (
                    <UI.Span>
                      <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                        <UI.Icon>
                          <XCircle />
                        </UI.Icon>
                      </UI.A>
                    </UI.Span>
                  )}
                </UI.InputWrapper>
              </UI.InputGroup>
            )}
          </>
        )}

        {filters.event && filters.event !== event?.id && (
          <UI.FlexContainer style={{ minHeight: 21 }} sc={{ alignItems: 'center' }}>
            <UI.Loader />
          </UI.FlexContainer>
        )}

        {event?.ticket_categories.length > 0 && enabled('tickets') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('tickets')}
              role="button"
              sc={{ mb: showingFilters.tickets ? 2 : 0 }}
            >
              {t('tickets')}
              {' '}
              {!showingFilters.tickets && filters.tickets?.length > 0 && (
                <UI.Badge>{formatNumber(filters.tickets.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.tickets && <ChevronUp />}
                {!showingFilters.tickets && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.tickets && event.ticket_categories.map((ticketCategory) => (
              <UI.Div key={ticketCategory.id}>
                {event.ticket_categories.length > 1 && (
                  <UI.Checkbox
                    checked={!!filters.ticket_categories?.includes(ticketCategory.id)}
                    onChange={() => toggleTicketCategoryFilter(ticketCategory.id)}
                    disabled={readOnly}
                  >
                    {ticketCategory.title}
                  </UI.Checkbox>
                )}
                {(event.ticket_categories.length === 1
                  || filters.ticket_categories?.includes(ticketCategory.id)) && (
                  <UI.Div sc={{ pl: event.ticket_categories.length > 1 ? 4 : 0 }}>
                    {ticketCategory.tickets.map((ticket) => (
                      <UI.Div key={ticket.id}>
                        <UI.Checkbox
                          checked={!!filters.tickets?.includes(ticket.id)}
                          onChange={() => toggleTicketFilter(ticket.id)}
                          disabled={readOnly}
                        >
                          {ticket.title}
                        </UI.Checkbox>
                        {filters.tickets?.includes(ticket.id)
                          && (ticket.promotions.length > 1 || ticket.time_slots.length > 0)
                          && (
                            <UI.Div sc={{ pl: 4 }}>
                              {ticket.promotions.length > 1 && ticket.promotions.map((promotion) => (
                                <UI.Checkbox
                                  checked={!!filters.promotions?.includes(promotion.id)}
                                  onChange={() => togglePromotionFilter(promotion.id)}
                                  disabled={readOnly}
                                  key={promotion.id}
                                >
                                  <UI.Icon sc={{ muted: true }}>
                                    <Tag />
                                  </UI.Icon>
                                  {' '}
                                  {promotion.title || t('standard')}
                                </UI.Checkbox>
                              ))}
                              {ticket.time_slots.length > 0 && ticket.time_slots.map((timeSlot) => (
                                <UI.Checkbox
                                  checked={!!filters.time_slots?.includes(timeSlot.id)}
                                  onChange={() => toggleTimeSlotFilter(timeSlot.id)}
                                  disabled={readOnly}
                                  key={timeSlot.id}
                                >
                                  <UI.Icon sc={{ muted: true }}>
                                    <Clock />
                                  </UI.Icon>
                                  {' '}
                                  <UI.Delimit>
                                    {formatTimeSlot(timeSlot)}
                                    {timeSlot.title}
                                  </UI.Delimit>
                                </UI.Checkbox>
                              ))}
                            </UI.Div>
                          )}
                      </UI.Div>
                    ))}
                  </UI.Div>
                )}
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event?.ticket_number_sequences.length > 0 && enabled('ticket_number_sequences') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('ticket_number_sequences')}
              role="button"
              sc={{ mb: showingFilters.ticket_number_sequences ? 2 : 0 }}
            >
              {t('ticket_numbers')}
              {' '}
              {!showingFilters.ticket_number_sequences
                  && (filters.ticket_number_sequences?.length > 0 || filters.has_ticket_number) && (
                  <UI.Badge>
                    {formatNumber(
                      (filters.ticket_number_sequences?.length || 0) + (filters.has_ticket_number ? 1 : 0),
                    )}
                  </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.ticket_number_sequences && <ChevronUp />}
                {!showingFilters.ticket_number_sequences && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.ticket_number_sequences && (
              <>
                {event.ticket_number_sequences.map((sequence) => (
                  <UI.Div key={sequence.id}>
                    <UI.Checkbox
                      checked={!!filters.ticket_number_sequences?.includes(sequence.id)}
                      onChange={() => toggleTicketNumberSequenceFilter(sequence.id)}
                      disabled={readOnly}
                    >
                      {sequence.title}
                    </UI.Checkbox>
                  </UI.Div>
                ))}
                <UI.Checkbox
                  checked={filters.has_ticket_number === false}
                  onChange={() => toggleHasTicketNumberFilter()}
                  disabled={readOnly}
                >
                  {t('without_ticket_number')}
                </UI.Checkbox>
              </>
            )}
          </UI.InputGroup>
        )}

        {products?.length > 0 && enabled('products') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('products')}
              role="button"
              sc={{ mb: showingFilters.products ? 2 : 0 }}
            >
              {t('products')}
              {' '}
              {!showingFilters.products && filters.products?.length > 0 && (
                <UI.Badge>{formatNumber(filters.products.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.products && <ChevronUp />}
                {!showingFilters.products && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.products && products.map((product) => (
              <UI.Div key={product.id}>
                <UI.Checkbox
                  checked={!!filters.products?.includes(product.id)}
                  onChange={() => toggleProductFilter(product.id)}
                  disabled={readOnly}
                >
                  {product.title}
                </UI.Checkbox>
                {filters.products?.includes(product.id)
                  && (product.promotions.length > 1 || product.product_variants.length > 0)
                  && (
                    <UI.Div sc={{ pl: 4 }}>
                      {product.promotions.length > 1 && product.promotions.map((promotion) => (
                        <UI.Checkbox
                          checked={!!filters.promotions?.includes(promotion.id)}
                          onChange={() => togglePromotionFilter(promotion.id)}
                          disabled={readOnly}
                          key={promotion.id}
                        >
                          <UI.Icon sc={{ muted: true }}>
                            <Tag />
                          </UI.Icon>
                          {' '}
                          {promotion.title || t('standard')}
                        </UI.Checkbox>
                      ))}
                      {product.product_variants.length > 0
                        && product.product_variants.map((productVariant) => (
                          <UI.Checkbox
                            checked={!!filters.product_variants?.includes(productVariant.id)}
                            onChange={() => toggleProductVariantFilter(productVariant.id)}
                            disabled={readOnly}
                            key={productVariant.id}
                          >
                            <SellableLabel type="product">
                              {productVariant.title}
                            </SellableLabel>
                          </UI.Checkbox>
                        ))}
                    </UI.Div>
                  )}
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {participantFields?.length > 0 && enabled('participant_fields') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('participant_fields')}
              role="button"
              sc={{ mb: showingFilters.participant_fields ? 2 : 0 }}
            >
              {t('custom_fields')}
              {' '}
              {!showingFilters.participant_fields
                && (filters.participant_field_choices?.length > 0 || filters.participant_fields?.length > 0) && (
                <UI.Badge>
                  {formatNumber(
                    (filters.participant_field_choices?.length || 0) + (filters.participant_fields?.length || 0),
                  )}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.participant_fields && <ChevronUp />}
                {!showingFilters.participant_fields && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.participant_fields && (
              <UI.Div>
                {participantFields.map((field) => (
                  <UI.Div key={field.id}>
                    <UI.Checkbox
                      checked={!!filters.participant_fields?.map(({ id }) => id).includes(field.id)}
                      onChange={() => toggleParticipantFieldFilter(field.id)}
                      disabled={readOnly}
                    >
                      <UI.Span sc={{ muted: !field.enabled }}>
                        {field.title}
                      </UI.Span>
                    </UI.Checkbox>
                    {filters.participant_fields?.map(({ id }) => id).includes(field.id) && (
                      <UI.Div sc={{ pl: 4 }}>
                        <ParticipantFieldFiltersForm
                          field={field}
                          filters={
                            filters.participant_fields.filter(
                              ({ id }) => id === field.id,
                            )[0].filters
                          }
                          onChange={(update) => setParticipantFieldFilters(field.id, update)}
                        />
                        {field.choices.map((choice) => (
                          <UI.Checkbox
                            checked={!!filters.participant_field_choices?.includes(choice.id)}
                            onChange={() => toggleParticipantFieldChoiceFilter(choice.id)}
                            disabled={readOnly}
                            key={choice.id}
                          >
                            <UI.Span sc={{ muted: !choice.enabled }}>
                              {choice.title}
                            </UI.Span>
                          </UI.Checkbox>
                        ))}
                      </UI.Div>
                    )}
                  </UI.Div>
                ))}
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {event && event.invitations.length > 0 && enabled('invitations') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('invitations')}
              role="button"
              sc={{ mb: showingFilters.invitations ? 2 : 0 }}
            >
              {t('invitation_codes')}
              {' '}
              {!showingFilters.invitations && filters.invitations?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.invitations.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.invitations && <ChevronUp />}
                {!showingFilters.invitations && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.invitations && event.invitations.map((invitation) => (
              <UI.Div key={invitation.id}>
                <UI.Checkbox
                  checked={!!filters.invitations?.includes(invitation.id)}
                  onChange={() => toggleInvitationFilter(invitation.id)}
                  disabled={readOnly}
                >
                  <UI.Label>
                    <UI.Strong>{invitation.prefix}</UI.Strong>
                  </UI.Label>
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.coupons.length > 0 && enabled('coupons') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('coupons')}
              role="button"
              sc={{ mb: showingFilters.coupons ? 2 : 0 }}
            >
              {t('coupons')}
              {' '}
              {!showingFilters.coupons && filters.coupons?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.coupons.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.coupons && <ChevronUp />}
                {!showingFilters.coupons && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.coupons && event.coupons.map((coupon) => (
              <UI.Div key={coupon.id}>
                <UI.Checkbox
                  checked={!!filters.coupons?.includes(coupon.id)}
                  onChange={() => toggleCouponFilter(coupon.id)}
                  disabled={readOnly}
                >
                  <UI.Label>
                    <UI.Strong>{coupon.prefix}</UI.Strong>
                  </UI.Label>
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.lotteries.length > 0 && enabled('lotteries') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('lotteries')}
              role="button"
              sc={{ mb: showingFilters.lotteries ? 2 : 0 }}
            >
              {t('selected_by_lottery')}
              {' '}
              {!showingFilters.lotteries && filters.lotteries?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.lotteries.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.lotteries && <ChevronUp />}
                {!showingFilters.lotteries && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.lotteries && event.lotteries.map((lottery) => (
              <UI.Div key={lottery.id}>
                <UI.Checkbox
                  checked={!!filters.lotteries?.includes(lottery.id)}
                  onChange={() => toggleLotteryFilter(lottery.id)}
                  disabled={readOnly}
                >
                  {lottery.title}
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.lotteries.length > 0 && enabled('rejected_lotteries') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('rejected_lotteries')}
              role="button"
              sc={{ mb: showingFilters.rejected_lotteries ? 2 : 0 }}
            >
              {t('rejected_by_lottery')}
              {' '}
              {!showingFilters.rejected_lotteries && filters.rejected_lotteries?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.rejected_lotteries.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.rejected_lotteries && <ChevronUp />}
                {!showingFilters.rejected_lotteries && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.rejected_lotteries && event.lotteries.map((lottery) => (
              <UI.Div key={lottery.id}>
                <UI.Checkbox
                  checked={!!filters.rejected_lotteries?.includes(lottery.id)}
                  onChange={() => toggleRejectedLotteryFilter(lottery.id)}
                  disabled={readOnly}
                >
                  {lottery.title}
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {enabled('team') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('team')}
              role="button"
              sc={{ mb: showingFilters.team ? 2 : 0 }}
            >
              {t('team')}
              {' '}
              {!showingFilters.team && typeof filters.has_team === 'boolean' && (
                <UI.Badge>
                  1
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.team && <ChevronUp />}
                {!showingFilters.team && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.team && (
              <UI.Div>
                <UI.Radio
                  checked={typeof filters.has_team !== 'boolean'}
                  onChange={() => toggleHasTeamFilter(null)}
                  disabled={readOnly}
                >
                  {t('all')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.has_team === true}
                  onChange={() => toggleHasTeamFilter(true)}
                  disabled={readOnly}
                >
                  {t('with_team')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.has_team === false}
                  onChange={() => toggleHasTeamFilter(false)}
                  disabled={readOnly}
                >
                  {t('without_team')}
                </UI.Radio>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('registration_date') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('registration_date')}
              role="button"
              sc={{ mb: showingFilters.registration_date ? 2 : 0 }}
            >
              {t('registration_date')}
              {' '}
              {!showingFilters.registration_date && filters.payment_statuses?.length > 0 && (
                <UI.Badge>
                  {formatNumber(
                    filters.created_after && filters.created_before ? 2 : (
                      filters.created_after || filters.created_before ? 1 : 0
                    ),
                  )}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.registration_date && <ChevronUp />}
                {!showingFilters.registration_date && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.registration_date && (
              <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                <UI.Div>
                  <UI.InputLabel htmlFor="CreatedAfter">
                    {t('date_after')}
                  </UI.InputLabel>
                  <UI.DatePicker
                    selected={filters.created_after}
                    maxDate={filters.created_before}
                    endDate={filters.created_before}
                    onChange={handleCreatedAfterChange}
                    disabled={readOnly}
                    id="CreatedAfter"
                  />
                </UI.Div>
                <UI.Div>
                  <UI.InputLabel htmlFor="CreatedBefore">
                    {t('date_before')}
                  </UI.InputLabel>
                  <UI.DatePicker
                    selected={filters.created_before}
                    minDate={filters.created_after}
                    startDate={filters.created_after}
                    onChange={handleCreatedBeforeChange}
                    disabled={readOnly}
                    id="CreatedBefore"
                  />
                </UI.Div>
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        )}

        {enabled('statuses') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('statuses')}
              role="button"
              sc={{ mb: showingFilters.statuses ? 2 : 0 }}
            >
              {t('registration_status')}
              {' '}
              {!showingFilters.statuses && filters.statuses?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.statuses.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.statuses && <ChevronUp />}
                {!showingFilters.statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={selectedActiveStatus}
                  onChange={() => toggleActiveFilter()}
                  disabled={readOnly}
                >
                  {t('active')}
                </UI.Checkbox>
                {selectedActiveStatus && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={!!filters.statuses?.includes(PurchaseStatus.complete)}
                      onChange={() => toggleStatusFilter(PurchaseStatus.complete)}
                      disabled={readOnly}
                    >
                      {t('complete')}
                    </UI.Checkbox>
                    <UI.Checkbox
                      checked={!!filters.statuses?.includes(PurchaseStatus.incomplete)}
                      onChange={() => toggleStatusFilter(PurchaseStatus.incomplete)}
                      disabled={readOnly}
                    >
                      {t('incomplete')}
                    </UI.Checkbox>
                  </UI.Div>
                )}
                <UI.Div>
                  <UI.Checkbox
                    checked={!!filters.statuses?.includes(PurchaseStatus.preregistration)}
                    onChange={() => toggleStatusFilter(PurchaseStatus.preregistration)}
                    disabled={readOnly}
                  >
                    {t('preregistration')}
                  </UI.Checkbox>
                </UI.Div>
                <UI.Div>
                  <UI.Checkbox
                    checked={!!filters.statuses?.includes(PurchaseStatus.waitlist)}
                    onChange={() => toggleStatusFilter(PurchaseStatus.waitlist)}
                    disabled={readOnly}
                  >
                    {t('waitlist')}
                  </UI.Checkbox>
                </UI.Div>
                <UI.Div>
                  <UI.Checkbox
                    checked={!!filters.statuses?.includes(PurchaseStatus.inactive)}
                    onChange={() => toggleStatusFilter(PurchaseStatus.inactive)}
                    disabled={readOnly}
                  >
                    {t('cancelled')}
                  </UI.Checkbox>
                </UI.Div>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('assignment_statuses') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('assignment_statuses')}
              role="button"
              sc={{ mb: showingFilters.assignment_statuses ? 2 : 0 }}
            >
              {t('assignment_status')}
              {' '}
              {!showingFilters.assignment_statuses && filters.assignment_statuses?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.assignment_statuses.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.assignment_statuses && <ChevronUp />}
                {!showingFilters.assignment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.assignment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={!!filters.assignment_statuses?.includes(AssignmentStatus.assigned)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.assigned)}
                  disabled={readOnly}
                >
                  {t('assigned')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.assignment_statuses?.includes(AssignmentStatus.invited)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.invited)}
                  disabled={readOnly}
                >
                  {t('invited')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.assignment_statuses?.includes(AssignmentStatus.unassigned)}
                  onChange={() => toggleAssignmentStatusFilter(AssignmentStatus.unassigned)}
                  disabled={readOnly}
                >
                  {t('unassigned')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('payment_statuses') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('payment_statuses')}
              role="button"
              sc={{ mb: showingFilters.payment_statuses ? 2 : 0 }}
            >
              {t('payment_status')}
              {' '}
              {!showingFilters.payment_statuses && filters.payment_statuses?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.payment_statuses.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.payment_statuses && <ChevronUp />}
                {!showingFilters.payment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.payment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.paid)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.paid)}
                  disabled={readOnly}
                >
                  {t('paid')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.open)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.open)}
                  disabled={readOnly}
                >
                  {t('open')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.refunded)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.refunded)}
                  disabled={readOnly}
                >
                  {t('refunded')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.has_balance)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.has_balance)}
                  disabled={readOnly}
                >
                  {t('credit')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('age') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('age')}
              role="button"
              sc={{ mb: showingFilters.age ? 2 : 0 }}
            >
              {t('age')}
              {' '}
              {!showingFilters.age && (filters.min_age || filters.max_age) && (
              <UI.Badge>
                {filters.min_age || 0}
                {filters.max_age && <>&ndash;</>}
                {!filters.max_age && '+'}
                {filters.max_age}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.age && <ChevronUp />}
                {!showingFilters.age && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.age && (
              <UI.FormGrid sc={{ columns: '1fr 1fr' }} style={{ maxWidth: 200 }}>
                <UI.Div>
                  <UI.InputLabel htmlFor="MinAge">
                    {t('min')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.min_age}
                    onChange={handleMinAgeChange}
                    maxLength={3}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MinAge"
                  />
                </UI.Div>
                <UI.Div>
                  <UI.InputLabel htmlFor="MaxAge">
                    {t('max')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.max_age}
                    onChange={handleMaxAgeChange}
                    maxLength={3}
                    inputMode="numeric"
                    match={/[\d]/g}
                    disabled={readOnly}
                    id="MaxAge"
                  />
                </UI.Div>
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        )}

        {enabled('gender') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('genders')}
              role="button"
              sc={{ mb: showingFilters.genders ? 2 : 0 }}
            >
              {t('gender')}
              {' '}
              {!showingFilters.genders && filters.genders?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.genders.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.genders && <ChevronUp />}
                {!showingFilters.genders && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.genders && (
              <>
                <UI.Checkbox
                  checked={!!filters.genders?.includes(Gender.male)}
                  onChange={() => toggleGenderFilter(Gender.male)}
                  disabled={readOnly}
                >
                  {t('common:genders.male')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.genders?.includes(Gender.female)}
                  onChange={() => toggleGenderFilter(Gender.female)}
                  disabled={readOnly}
                >
                  {t('common:genders.female')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.genders?.includes(Gender.neutral)}
                  onChange={() => toggleGenderFilter(Gender.neutral)}
                  disabled={readOnly}
                >
                  <UI.Span sc={{ color: !project.genders.includes(Gender.neutral) ? 'gray.400' : undefined }}>
                    {t('common:genders.neutral')}
                  </UI.Span>
                  {!project.genders.includes(Gender.neutral) && (
                    <ProtectedSection permissions={[Permission.admin]}>
                      {' '}
                      <UI.Link
                        to={`/${project.id}/project/features`}
                        sc={{ secondary: true, color: 'gray.400' }}
                      >
                        <UI.Icon>
                          <Settings />
                        </UI.Icon>
                      </UI.Link>
                    </ProtectedSection>
                  )}
                </UI.Checkbox>
              </>
            )}
          </UI.InputGroup>
        )}

        {enabled('locales') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.locales.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locale_${locale}`}
                checked={!!filters.locales?.includes(locale)}
                onChange={() => toggleLocaleFilter(locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {mailPermissions.length > 0 && enabled('mail_opt_ins') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge>{filters.mail_opt_ins.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && mailPermissions.map((mailPermission) => (
              <UI.Checkbox
                onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                checked={!!filters.mail_opt_ins?.includes(mailPermission.id)}
                disabled={readOnly}
                key={mailPermission.id}
              >
                {mailPermission.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {(showTotals || showResetButton) && (
          <>
            {showTotals && (
              <UI.Card
                sc={{
                  spacing: 'xs',
                  outline: 'gray.200',
                  background: 'gray.50',
                  loading: loadingStats,
                  borderRadius: 'md',
                  small: true,
                }}
              >
                <UI.Delimit>
                  <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                    <UI.Icon sc={{ mr: 1, mb: 0.25 }}>
                      {loadingStats ? <UI.Loader sc={{ size: 14 }} /> : <UI.Icons.Ticket />}
                    </UI.Icon>
                    <Trans
                      i18nKey="n_registrations_in_selection"
                      values={{
                        count: stats?.tickets || 0,
                        value: formatNumber(stats?.tickets || 0),
                      }}
                    >
                      <UI.Strong />
                    </Trans>
                  </UI.Span>
                  {showViewRegistrationsButton && (
                    <UI.A href={`/b/${project.id}/participants?${encodeUrl(filters)}`} target="_blank">
                      {t('view')}
                      {' '}
                      <UI.Icon sc={{ mb: 1 / 3 }}>
                        <ExternalLink />
                      </UI.Icon>
                    </UI.A>
                  )}
                </UI.Delimit>
              </UI.Card>
            )}

            {showResetButton && hasFilters && (
              <UI.Div>
                <UI.Button onClick={() => resetFilters()}>
                  {t('reset_filters')}
                </UI.Button>
              </UI.Div>
            )}
          </>
        )}
      </UI.Delimit>
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export const useRegistrationFilterMapping = () => {
  const { formatDate, parseDate } = useLocale();

  // 2026-04-22 (local) => 2026-04-22 00:00:00 (local) => 2026-04-21 22:00:00 (UTC)
  const dateAfterToServer = (after: string) => formatDate(
    parseDate(after, { format: 'internal_date' }),
    { format: 'internal_date_time', timezone: 'UTC' },
  );

  // 2026-04-23 (local) => 2026-04-24 00:00:00 (local) => 2026-04-23 22:00:00 (UTC)
  const dateBeforeToServer = (before: string) => {
    const date = parseDate(before, { format: 'internal_date' });
    date.setDate(date.getDate() + 1);
    return formatDate(date, { format: 'internal_date_time', timezone: 'UTC' });
  };

  // 2026-04-21 22:00:00 (UTC) => 2026-04-22 00:00:00 (local) => 2026-04-22 (local)
  const dateAfterToFilter = (after: string) => formatDate(
    parseDate(after, { format: 'internal_date_time', timezone: 'UTC' }),
    { format: 'internal_date' },
  );

  // 2026-04-23 22:00:00 (UTC) => 2026-04-24 00:00:00 (local) => 2026-04-23 (local)
  const dateBeforeToFilter = (before: string) => {
    const date = parseDate(before, { format: 'internal_date_time', timezone: 'UTC' });
    date.setDate(date.getDate() - 1);
    return formatDate(date, { format: 'internal_date' });
  };

  const mapRegistrationFiltersToServerInput = (filters: RegistrationFilterParams): RegistrationFiltersType => {
    const participantFilters = {
      locales: filters.locales ?? undefined,
      mail_opt_ins: filters.mail_opt_ins ?? undefined,
    };

    return {
      events: filters.event ? [filters.event] : undefined,
      search: filters.search ?? undefined,
      min_age: filters.min_age ?? undefined,
      max_age: filters.max_age ?? undefined,
      genders: filters.genders ?? undefined,
      promotions: filters.promotions ?? undefined,
      ticket_categories: filters.ticket_categories ?? undefined,
      tickets: filters.tickets ?? undefined,
      time_slots: filters.time_slots ?? undefined,
      products: filters.products ?? undefined,
      product_variants: filters.product_variants ?? undefined,
      created_after: filters.created_after ? dateAfterToServer(filters.created_after) : undefined,
      created_before: filters.created_before ? dateBeforeToServer(filters.created_before) : undefined,
      statuses: filters.statuses ?? undefined,
      assignment_statuses: filters.assignment_statuses ?? undefined,
      payment_statuses: filters.payment_statuses ?? undefined,
      participant_fields: filters.participant_fields ?? undefined,
      participant_field_choices: filters.participant_field_choices ?? undefined,
      ticket_number_sequences: filters.ticket_number_sequences ?? undefined,
      has_ticket_number: filters.has_ticket_number ?? undefined,
      has_team: filters.has_team ?? undefined,
      invitations: filters.invitations ?? undefined,
      coupons: filters.coupons ?? undefined,
      lotteries: filters.lotteries ?? undefined,
      rejected_lotteries: filters.rejected_lotteries ?? undefined,

      // Subfilters
      participant_filters: !isEmpty(participantFilters) ? participantFilters : undefined,
    };
  };

  const mapServerOutputToRegistrationFilters = (filters: RegistrationFiltersType): RegistrationFilterParams => ({
    event: filters.events?.[0],
    assignment_statuses: filters.assignment_statuses ?? undefined,
    coupons: filters.coupons ?? undefined,
    genders: filters.genders ?? undefined,
    has_team: filters.has_team ?? undefined,
    has_ticket_number: filters.has_ticket_number ?? undefined,
    invitations: filters.invitations ?? undefined,
    lotteries: filters.lotteries ?? undefined,
    max_age: filters.max_age ?? undefined,
    min_age: filters.min_age ?? undefined,
    participant_fields: filters.participant_fields?.map((filter) => ({
      id: filter.id,
      filters: filter.filters ? omit(filter.filters, '__typename') : null,
    })) ?? undefined,
    participant_field_choices: filters.participant_field_choices ?? undefined,
    payment_statuses: filters.payment_statuses ?? undefined,
    product_variants: filters.product_variants ?? undefined,
    products: filters.products ?? undefined,
    promotions: filters.promotions ?? undefined,
    rejected_lotteries: filters.rejected_lotteries ?? undefined,
    search: filters.search ?? undefined,
    created_after: filters.created_after ? dateAfterToFilter(filters.created_after) : undefined,
    created_before: filters.created_before ? dateBeforeToFilter(filters.created_before) : undefined,
    statuses: filters.statuses ?? undefined,
    ticket_categories: filters.ticket_categories ?? undefined,
    ticket_number_sequences: filters.ticket_number_sequences ?? undefined,
    tickets: filters.tickets ?? undefined,
    time_slots: filters.time_slots ?? undefined,

    // Participant filters
    locales: filters.participant_filters?.locales ?? undefined,
    mail_opt_ins: filters.participant_filters?.mail_opt_ins ?? undefined,
  });

  const parseRegistrationFiltersUrlState = (params: UrlState): RegistrationFilterParams => ({
    event: parseHelpers.string(params.event),
    search: parseHelpers.string(params.search),
    min_age: parseHelpers.integer(params.min_age),
    max_age: parseHelpers.integer(params.max_age),
    genders: parseHelpers.array(params.genders, (value) => (parseHelpers.string(value) in Gender ? value : null), null),
    locales: parseHelpers.array(params.locales, parseHelpers.string, null),
    mail_opt_ins: parseHelpers.array(params.mail_opt_ins, parseHelpers.string, null),
    ticket_categories: parseHelpers.array(params.ticket_categories, parseHelpers.string, null),
    tickets: parseHelpers.array(params.tickets, parseHelpers.string, null),
    time_slots: parseHelpers.array(params.time_slots, parseHelpers.string, null),
    products: parseHelpers.array(params.products, parseHelpers.string, null),
    product_variants: parseHelpers.array(params.product_variants, parseHelpers.string, null),
    promotions: parseHelpers.array(params.promotions, parseHelpers.string, null),
    invitations: parseHelpers.array(params.invitations, parseHelpers.string, null),
    coupons: parseHelpers.array(params.coupons, parseHelpers.string, null),
    lotteries: parseHelpers.array(params.lotteries, parseHelpers.string, null),
    rejected_lotteries: parseHelpers.array(params.rejected_lotteries, parseHelpers.string, null),
    created_after: parseHelpers.date(params.created_after),
    created_before: parseHelpers.date(params.created_before),
    statuses: parseHelpers.array(params.statuses, (value) => parseHelpers.enumeration(value, PurchaseStatus), null),
    assignment_statuses: parseHelpers.array(
      params.assignment_statuses,
      (value) => parseHelpers.enumeration(value, AssignmentStatus),
      null,
    ),
    payment_statuses: parseHelpers.array(
      params.payment_statuses,
      (value) => parseHelpers.enumeration(value, PurchasePaymentStatus),
      null),
    participant_fields: parseHelpers.array(
      params.participant_fields,
      (value) => (typeof value === 'object' ? value : null),
      null,
    ),
    participant_field_choices: parseHelpers.array(params.participant_field_choices, parseHelpers.string, null),
    ticket_number_sequences: parseHelpers.array(params.ticket_number_sequences, parseHelpers.string, null),
    has_ticket_number: parseHelpers.boolean(params.has_ticket_number),
    has_team: parseHelpers.boolean(params.has_team),
  });

  return {
    mapRegistrationFiltersToServerInput,
    mapServerOutputToRegistrationFilters,
    parseRegistrationFiltersUrlState,
  };
};

export default RegistrationFilters;
